<template>
  <v-container v-if="currentExhibitor">
    <v-row dense>
      <v-col
        cols="12"
        class="title"
        v-html="$t('exhibitionArea.virtualExhibitionHeadline')"
      />
      <v-col>
        <v-chip
          outlined
          :color="currentExhibitor.approved ? 'success' : 'error'"
        >
          {{
            $t(
              `exhibitionArea.${
                currentExhibitor.approved ? "approved" : "notApproved"
              }`
            )
          }}
        </v-chip>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.logo')" />
      <v-col cols="12" v-if="!inputFields.logo">
        <v-file-input
          color="primary"
          outlined
          accept="image/*"
          v-model="inputFields.logo"
          :label="$t('exhibitionArea.logo')"
          :loading="updating"
        />
      </v-col>
      <v-col cols="12" v-else>
        <v-img
          :src="getImageUrl(inputFields.logo)"
          max-width="300"
          max-height="300"
          contain
        >
          <v-btn
            fab
            color="primary"
            @click="inputFields.logo = null"
            :loading="updating"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-img>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.color')" />
      <v-col
        cols="12"
        class="text-body-1"
        v-html="$t('exhibitionArea.colorDescription')"
      />
      <v-col cols="12">
        <v-color-picker
          mode="hexa"
          v-model="inputFields.color"
          :loading="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col
        cols="12"
        class="text-h6"
        v-html="$t('exhibitionArea.description')"
      />
      <v-col cols="12">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.description') + ' (de)'"
        />
        <ckeditor
          v-model="inputFields.description_de"
          :config="getCopiedEditorConfig('description_de')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.description') + ' (en)'"
        />
        <ckeditor
          v-model="inputFields.description_en"
          :config="getCopiedEditorConfig('description_en')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12" v-if="hasSpanish">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.description') + ' (es)'"
        />
        <div class="text-body-2" v-html="$t('exhibitionArea.hintSpanish')" />
        <ckeditor
          v-model="inputFields.description_es"
          :config="getCopiedEditorConfig('description_es')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.videos')" />
      <v-col
        cols="12"
        class="text-body-1"
        v-html="$t('exhibitionArea.videosPicturesDescription')"
      />
    </v-row>
    <v-row
      dense
      v-for="(video, index) in inputFields.videos"
      :key="`video-${index}`"
      class="mb-n8"
    >
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.youtubeUrl') + ' *'"
          v-model="video.video"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="2">
        <v-btn
          tile
          large
          color="primary"
          icon
          @click="inputFields.videos.splice(index, 1)"
          class="mt-1"
          :loading="updating"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-btn
          color="primary"
          @click="inputFields.videos.push({ video: '' })"
          :loading="updating"
        >
          <v-icon>mdi-plus</v-icon>
          {{ $t("exhibitionArea.video") }}
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.pictures')" />
      <v-col
        cols="12"
        class="text-body-1"
        v-html="$t('exhibitionArea.videosPicturesDescription')"
      />
    </v-row>
    <v-row
      dense
      v-for="(picture, index) in inputFields.pictures"
      :key="`picture-${index}`"
      class="mb-n4"
    >
      <v-col cols="12">
        <v-img
          :src="getImageUrl(picture.file)"
          max-width="300"
          max-height="300"
          contain
        >
          <v-btn
            fab
            color="primary"
            @click="inputFields.pictures.splice(index, 1)"
            :loading="updating"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-img>
      </v-col>
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.pictureTitle') + ' (de)'"
          v-model="picture.title"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.pictureTitle') + ' (en)'"
          v-model="picture.title_en"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col v-if="hasSpanish">
        <v-text-field
          :label="$t('exhibitionArea.pictureTitle') + ' (es)'"
          v-model="picture.title_es"
          :hint="$t('exhibitionArea.hintSpanish')"
          persistent-hint
          dense
          outlined
          :loading="updating"
        />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-file-input
          ref="pictureUpload"
          color="primary"
          outlined
          accept="image/*"
          @change="addPictureFile"
          :label="$t('exhibitionArea.picture')"
          :loading="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col
        cols="12"
        class="text-h6"
        v-html="$t('exhibitionArea.virtualMeeting')"
      />
      <v-col cols="12">
        <v-text-field
          :label="$t('exhibitionArea.meetingUrl')"
          v-model="inputFields.meetingUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="12" class="mt-n4">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.meetingUrlInfo') + ' (de)'"
        />
        <ckeditor
          v-model="inputFields.meetingUrlInfo_de"
          :config="getCopiedEditorConfig('meetingUrlInfo_de')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.meetingUrlInfo') + ' (en)'"
        />
        <ckeditor
          v-model="inputFields.meetingUrlInfo_en"
          :config="getCopiedEditorConfig('meetingUrlInfo_en')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12" v-if="hasSpanish">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.meetingUrlInfo') + ' (es)'"
        />
        <div class="text-body-2" v-html="$t('exhibitionArea.hintSpanish')" />
        <ckeditor
          v-model="inputFields.meetingUrlInfo_es"
          :config="getCopiedEditorConfig('meetingUrlInfo_es')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.flyer')" />
      <v-col
        cols="12"
        class="text-body-1"
        v-html="$t('exhibitionArea.flyerDescription')"
      />
    </v-row>
    <v-row
      dense
      v-for="(flyer, index) in inputFields.flyer"
      :key="`flyer-${index}`"
      class="mb-n4"
    >
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.flyerTitle') + ' (de)'"
          v-model="flyer.title"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.flyerTitle') + ' (en)'"
          v-model="flyer.title_en"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col v-if="hasSpanish">
        <v-text-field
          :label="$t('exhibitionArea.flyerTitle') + ' (es)'"
          v-model="flyer.title_es"
          :hint="$t('exhibitionArea.hintSpanish')"
          persistent-hint
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col class="mt-1">
        <v-btn
          outlined
          color="primary"
          @click="downloadDocument(flyer.file)"
          class="mr-2"
          :loading="updating"
          v-if="flyer.file"
        >
          <v-icon>mdi-file-download</v-icon> DE
        </v-btn>
        <v-btn
          outlined
          color="primary"
          @click="downloadDocument(flyer.file_en)"
          class="mr-2"
          :loading="updating"
          v-if="flyer.file_en"
        >
          <v-icon>mdi-file-download</v-icon> EN
        </v-btn>
        <v-btn
          outlined
          color="primary"
          @click="downloadDocument(flyer.file_es)"
          class="mr-2"
          :loading="updating"
          v-if="flyer.file_es"
        >
          <v-icon>mdi-file-download</v-icon> ES
        </v-btn>
        <v-btn
          tile
          icon
          color="primary"
          :loading="updating"
          @click="inputFields.flyer.splice(index, 1)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row :class="inputFields.flyer.length ? 'mt-6' : ''" dense>
      <v-col>
        <v-file-input
          v-model="flyerFileDe"
          color="primary"
          outlined
          accept="application/pdf"
          label="PDF (de)"
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-file-input
          v-model="flyerFileEn"
          color="primary"
          outlined
          accept="application/pdf"
          label="PDF (en)"
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-file-input
          v-model="flyerFileEs"
          color="primary"
          outlined
          accept="application/pdf"
          label="PDF (es)"
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-btn
          color="primary"
          :disabled="!flyerFileDe && !flyerFileEn && !flyerFileEs"
          @click="addFlyerFile"
          fab
          :loading="updating"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="12" class="mt-n4">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.flyerInfo') + ' (de)'"
        />
        <ckeditor
          v-model="inputFields.flyerInfo_de"
          :config="getCopiedEditorConfig('flyerInfo_de')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.flyerInfo') + ' (en)'"
        />
        <ckeditor
          v-model="inputFields.flyerInfo_en"
          :config="getCopiedEditorConfig('flyerInfo_en')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12" v-if="hasSpanish">
        <div
          class="text-body-1"
          v-html="$t('exhibitionArea.flyerInfo') + ' (es)'"
        />
        <div class="text-body-2" v-html="$t('exhibitionArea.hintSpanish')" />
        <ckeditor
          v-model="inputFields.flyerInfo_es"
          :config="getCopiedEditorConfig('flyerInfo_es')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.contact')" />
      <v-col cols="12">
        <div class="text-body-1" v-html="$t('exhibitionArea.address')" />
        <ckeditor
          v-model="inputFields.address"
          :config="getCopiedEditorConfig('address')"
          :read-only="updating"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.phone')"
          v-model="inputFields.phone"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.facebookUrl')"
          v-model="inputFields.facebookUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          :label="$t('exhibitionArea.twitterUrl')"
          v-model="inputFields.twitterUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="4" class="mt-n6 mb-n6">
        <v-text-field
          :label="$t('exhibitionArea.youTubeUrl')"
          v-model="inputFields.youTubeUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="4" class="mt-n6 mb-n6">
        <v-text-field
          :label="$t('exhibitionArea.linkedInUrl')"
          v-model="inputFields.linkedInUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="4" class="mt-n6 mb-n6">
        <v-text-field
          :label="$t('exhibitionArea.xingUrl')"
          v-model="inputFields.xingUrl"
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="text-h6" v-html="$t('exhibitionArea.tags')" />
    </v-row>
    <v-row
      dense
      v-for="(tag, index) in inputFields.tags"
      :key="`tag-${index}`"
      class="mb-n4"
    >
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.tag') + ' (de)'"
          v-model="tag.title"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col>
        <v-text-field
          :label="$t('exhibitionArea.tag') + ' (en)'"
          v-model="tag.title_en"
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col v-if="hasSpanish">
        <v-text-field
          :label="$t('exhibitionArea.tag') + ' (es)'"
          v-model="tag.title_es"
          :hint="$t('exhibitionArea.hintSpanish')"
          persistent-hint
          dense
          outlined
          :loading="updating"
        />
      </v-col>
      <v-col cols="2">
        <v-btn
          tile
          large
          color="primary"
          icon
          @click="inputFields.tags.splice(index, 1)"
          :loading="updating"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-btn
          color="primary"
          @click="
            inputFields.tags.push({ title: '', title_en: '', title_es: '' })
          "
          :loading="updating"
        >
          <v-icon>mdi-plus</v-icon>
          {{ $t("exhibitionArea.tag") }}
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <v-col cols="12">
        <v-btn
          class="mb-4"
          color="primary"
          :loading="updating"
          :disabled="!hasChanges"
          @click="uploadData"
          large
        >
          {{ $t("exhibitionArea.save") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="showConfirmationDialog" width="500">
      <v-card>
        <v-card-title class="white--text headline primary" primary-title>
          {{ $t("exhibitionArea.uploadConfirmationTitle") }}
        </v-card-title>
        <v-card-text class="pa-md-4">
          {{ $t("exhibitionArea.uploadConfirmationText") }}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reload"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { attachmentResource } from "./../api/resources";

export default {
  data: () => ({
    editorConfigMap: new Map(),
    currentExhibitor: null,

    inputFields: {
      logo: null,
      color: "",
      description_de: "",
      description_en: "",
      description_es: "",
      meetingUrl: "",
      meetingUrlInfo_de: "",
      meetingUrlInfo_en: "",
      meetingUrlInfo_es: "",
      flyerInfo_de: "",
      flyerInfo_en: "",
      flyerInfo_es: "",
      address: "",
      phone: "",
      facebookUrl: "",
      twitterUrl: "",
      youTubeUrl: "",
      linkedInUrl: "",
      xingUrl: "",
      videos: [],
      pictures: [],
      flyer: [],
      tags: [],
    },
    flyerFileDe: null,
    flyerFileEn: null,
    flyerFileEs: null,
    initialInputFields: null,
    showConfirmationDialog: false,
    updating: false,
  }),

  created() {
    this.currentExhibitor = this.booths[0];
    this.richTextFields.forEach((field) => {
      if (
        this.currentExhibitor[field] &&
        this.currentExhibitor[field].items &&
        this.currentExhibitor[field].items.length
      ) {
        this.inputFields[field] = this.currentExhibitor[field].items[0].content;
      }
    });
    this.inputFields.color = this.currentExhibitor.color;
    this.inputFields.phone = this.currentExhibitor.phone;
    this.inputFields.facebookUrl = this.currentExhibitor.facebookUrl;
    this.inputFields.twitterUrl = this.currentExhibitor.twitterUrl;
    this.inputFields.youTubeUrl = this.currentExhibitor.youTubeUrl;
    this.inputFields.linkedInUrl = this.currentExhibitor.linkedInUrl;
    this.inputFields.xingUrl = this.currentExhibitor.xingUrl;
    this.inputFields.meetingUrl = this.currentExhibitor.meetingUrl;
    this.inputFields.videos = this.currentExhibitor.videos || [];
    this.inputFields.pictures = this.currentExhibitor.pictures || [];
    this.inputFields.flyer = this.currentExhibitor.flyer || [];
    this.inputFields.tags = this.currentExhibitor.tags || [];
    this.inputFields.logo = this.currentExhibitor.logo;
    this.initialInputFields = JSON.stringify(this.inputFields);
  },

  methods: {
    ...mapActions(["updateExhibitorVirtualExhibitionData"]),
    addPictureFile(file) {
      if (!file) {
        return;
      }
      this.inputFields.pictures.push({
        file,
        title: "",
        title_en: "",
        title_es: "",
      });
      this.$refs.pictureUpload.reset();
    },
    addFlyerFile() {
      this.inputFields.flyer.push({
        title: "",
        title_en: "",
        title_es: "",
        file: this.flyerFileDe,
        file_en: this.flyerFileEn,
        file_es: this.flyerFileEs,
      });
      this.flyerFileDe = null;
      this.flyerFileEn = null;
      this.flyerFileEs = null;
    },
    getDocumentUrl(document) {
      let fileUrl = document._url;
      if (!fileUrl && document._urls) {
        fileUrl = document._urls.original;
      }
      return fileUrl;
    },
    downloadDocument(document) {
      window.open(
        document._id
          ? this.getDocumentUrl(document)
          : URL.createObjectURL(document),
        "_blank"
      );
    },
    getImageUrl(imageObject) {
      return imageObject._id
        ? this.getDocumentUrl(imageObject)
        : URL.createObjectURL(imageObject);
    },
    async uploadData() {
      this.updating = true;
      const transformedData = {};
      this.richTextFields.forEach((field) => {
        transformedData[field] = {
          items: this.inputFields[field]
            ? [
                {
                  type: "apostrophe-rich-text",
                  content: this.inputFields[field],
                },
              ]
            : [],
          type: "area",
        };
      });
      if (this.inputFields.logo) {
        if (this.inputFields.logo._id) {
          transformedData.logo = this.inputFields.logo;
        } else {
          const formData = new FormData();
          formData.append("file", this.inputFields.logo);
          transformedData.logo = (
            await attachmentResource.post("", formData, {
              headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
            })
          ).data;
        }
      }
      if (this.inputFields.color) {
        transformedData.color =
          this.inputFields.color.hex || this.inputFields.color;
      }
      transformedData.videos = this.inputFields.videos;
      transformedData.pictures = [];
      for (const picture of this.inputFields.pictures) {
        if (!picture.file) {
          continue;
        }
        const pictureToAdd = {
          title: picture.title,
          title_en: picture.title_en || picture.title,
          title_es: picture.title_es || picture.title_en || picture.title,
        };
        if (picture.file._id) {
          pictureToAdd.file = picture.file;
        } else {
          const formData = new FormData();
          formData.append("file", picture.file);
          pictureToAdd.file = (
            await attachmentResource.post("", formData, {
              headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
            })
          ).data;
        }
        transformedData.pictures.push(pictureToAdd);
      }
      transformedData.meetingUrl = this.inputFields.meetingUrl;
      transformedData.flyer = [];
      for (const flyer of this.inputFields.flyer) {
        if (!flyer.file && !flyer.file_en && !flyer.file_es) {
          continue;
        }
        const flyerToAdd = {
          title: flyer.title || flyer.title_en || flyer.title_es,
          title_en: flyer.title_en || flyer.title,
          title_es: flyer.title_es || flyer.title_en || flyer.title,
        };
        let filename = "";
        for (const fileKey of ["file", "file_en", "file_es"]) {
          if (!flyer[fileKey]) {
            continue;
          }
          if (flyer[fileKey]._id) {
            flyerToAdd[fileKey] = flyer[fileKey];
            filename = filename || flyer[fileKey].title;
          } else {
            const formData = new FormData();
            formData.append("file", flyer[fileKey]);
            flyerToAdd[fileKey] = (
              await attachmentResource.post("", formData, {
                headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
              })
            ).data;
            filename = filename || flyerToAdd[fileKey].title;
          }
        }
        flyerToAdd.file =
          flyerToAdd.file || flyerToAdd.file_en || flyerToAdd.file_es;
        if (!flyerToAdd.title) {
          flyerToAdd.title = filename;
        }
        transformedData.flyer.push(flyerToAdd);
      }
      transformedData.phone = this.inputFields.phone;
      transformedData.facebookUrl = this.inputFields.facebookUrl;
      transformedData.twitterUrl = this.inputFields.twitterUrl;
      transformedData.youTubeUrl = this.inputFields.youTubeUrl;
      transformedData.linkedInUrl = this.inputFields.linkedInUrl;
      transformedData.xingUrl = this.inputFields.xingUrl;
      transformedData.tags = this.inputFields.tags
        .filter((entry) => entry.title || entry.title_en || entry.title_es)
        .map((entry) => {
          entry.title = entry.title || entry.title_en || entry.title_es;
          entry.title_en = entry.title_en || entry.title || entry.title_es;
          entry.title_es = entry.title_es || entry.title_en || entry.title;
          return entry;
        });
      await this.updateExhibitorVirtualExhibitionData(transformedData);
      this.updating = false;
      this.showConfirmationDialog = true;
    },
    reload() {
      location.reload();
    },
    getCopiedEditorConfig(fieldName) {
      if (this.editorConfigMap.has(fieldName)) {
        return this.editorConfigMap.get(fieldName);
      }
      this.editorConfigMap.set(fieldName, {
        toolbar: [[...this.editorDefaultToolbar]],
      });
      return this.editorConfigMap.get(fieldName);
    },
  },

  computed: {
    ...mapGetters(["pageType", "booths", "editorDefaultToolbar"]),
    hasSpanish() {
      return this.pageType === "ISRM";
    },
    hasChanges() {
      return JSON.stringify(this.inputFields) !== this.initialInputFields;
    },
    richTextFields() {
      return [
        "description_de",
        "description_en",
        "meetingUrlInfo_de",
        "meetingUrlInfo_en",
        "flyerInfo_de",
        "flyerInfo_en",
        "address",
        ...(this.hasSpanish
          ? ["description_es", "meetingUrlInfo_es", "flyerInfo_es"]
          : []),
      ];
    },
  },
};
</script>
